import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase, CITY_NAME } from '@/config/supabase'
import { Card, CardContent, Button } from '@/components/ui'
import { RefreshCw, Phone, PhoneCall, Mail, MapPin, Home, Calendar, Euro, Building2, Users, TrendingUp, Settings, CheckCircle, Trash2, BarChart3, Search, StickyNote, UserCheck, Bell, BellOff, FileText, LogOut, Bot, Sliders, Star, MessageSquare, Tag, X, Clock } from 'lucide-react'
import { QuotazioniEditor } from '@/components/admin/QuotazioniEditor'
import { OmiCorrezioniPanel } from '@/components/admin/OmiCorrezioniPanel'
import { StatisticheDashboard } from '@/components/admin/StatisticheDashboard'
import { ImpostazioniPanel } from '@/components/admin/ImpostazioniPanel'
import { ConversazioniPanel } from '@/components/admin/ConversazioniPanel'
import { ExportDropdown } from '@/components/admin/ExportDropdown'
import { DashboardOggi } from '@/components/admin/DashboardOggi'
import { LeadTimeline } from '@/components/admin/LeadTimeline'
import { logLeadActivity } from '@/lib/lead-activity'
import { TemplateMessages } from '@/components/admin/TemplateMessages'
import { useLeadsRealtime } from '@/hooks/useLeadsRealtime'
import { exportSingleLeadPDF } from '@/lib/export'
import { logout } from '@/lib/auth'
import { computeLeadScore, scoreColor, buildWhatsAppUrl, getTagColor, TAG_PRESETS, formatCallbackDate, isCallbackOverdue } from '@/lib/lead-utils'
import type { LeadWithValuation } from '@/types'
import type { ZoneQuotazione } from '@/data/zone-quotazioni'
import { fetchZoneQuotazioni } from '@/services/zone-quotazioni.service'

export function Admin() {
  const navigate = useNavigate()
  const [activeTab, setActiveTabRaw] = useState<'oggi' | 'leads' | 'quotazioni' | 'correzioni' | 'statistiche' | 'conversazioni' | 'impostazioni'>('oggi')
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(false)
  const [leads, setLeads] = useState<LeadWithValuation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Notes editing
  const [editingNotes, setEditingNotes] = useState<string | null>(null)
  const [notesDraft, setNotesDraft] = useState('')

  // Search & filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'contacted' | 'callback' | 'acquired'>('all')
  const [zoneFilter, setZoneFilter] = useState<string>('all')

  // Quotazioni dirty tracking + zones for export
  const [quotazioniDirty, setQuotazioniDirty] = useState(false)
  const [exportZones, setExportZones] = useState<ZoneQuotazione[]>([])

  // New admin features state
  const [callbackPickerLeadId, setCallbackPickerLeadId] = useState<string | null>(null)
  const [callbackDateDraft, setCallbackDateDraft] = useState('')
  const [tagPickerLeadId, setTagPickerLeadId] = useState<string | null>(null)
  const [templateLeadId, setTemplateLeadId] = useState<string | null>(null)
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'date' | 'score'>('date')

  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  )

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) return
    const permission = await Notification.requestPermission()
    setNotificationPermission(permission)
  }

  const handleNewLead = useCallback((lead: LeadWithValuation) => {
    setLeads((prev) => [lead, ...prev])
  }, [])

  const { isConnected, newLeadsCount, resetNewLeadsCount } = useLeadsRealtime({
    onNewLead: handleNewLead,
  })

  const fetchLeads = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch leads with their valuations
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads_cagliari')
        .select(`
          id,
          email,
          phone,
          first_name,
          last_name,
          gdpr_consent,
          marketing_consent,
          status,
          notes,
          source,
          callback_date,
          tags,
          created_at,
          valuation_id
        `)
        .order('created_at', { ascending: false })

      if (leadsError) throw leadsError

      // Fetch valuations for each lead
      const leadsWithValuations = await Promise.all(
        (leadsData || []).map(async (lead) => {
          if (lead.valuation_id) {
            const { data: valuation } = await supabase
              .from('valuations_cagliari')
              .select('*')
              .eq('id', lead.valuation_id)
              .single()

            return { ...lead, valuation }
          }
          return { ...lead, valuation: null }
        })
      )

      setLeads(leadsWithValuations)
    } catch (err) {
      console.error('Error fetching leads:', err)
      setError('Errore nel caricamento dei dati')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchLeads()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const updateLeadStatus = async (id: string, status: string) => {
    const currentLead = leads.find((l) => l.id === id)
    const { error: updateError } = await supabase
      .from('leads_cagliari')
      .update({ status })
      .eq('id', id)

    if (updateError) {
      console.error('Error updating lead status:', updateError)
      setError('Errore nell\'aggiornamento dello stato')
      return
    }

    setLeads((prev) =>
      prev.map((lead) => (lead.id === id ? { ...lead, status } : lead))
    )
    logLeadActivity(id, 'status_changed', { from: currentLead?.status || 'unknown', to: status })
  }

  const deleteLead = async (id: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo lead?')) return

    const { error: deleteError } = await supabase
      .from('leads_cagliari')
      .delete()
      .eq('id', id)

    if (deleteError) {
      console.error('Error deleting lead:', deleteError)
      setError('Errore nell\'eliminazione del lead')
      return
    }

    setLeads((prev) => prev.filter((lead) => lead.id !== id))
  }

  const saveNotes = async (id: string) => {
    const { error: updateError } = await supabase
      .from('leads_cagliari')
      .update({ notes: notesDraft || null })
      .eq('id', id)

    if (updateError) {
      console.error('Error saving notes:', updateError)
      setError('Errore nel salvataggio delle note')
      return
    }

    setLeads((prev) =>
      prev.map((lead) => (lead.id === id ? { ...lead, notes: notesDraft || null } : lead))
    )
    setEditingNotes(null)
    logLeadActivity(id, 'note_added', { preview: (notesDraft || '').slice(0, 60) })
  }

  // ─── Callback (Promemoria Follow-up) ────────────────────────────────────
  const saveCallback = async (id: string) => {
    const dateValue = callbackDateDraft || null
    const { error: updateError } = await supabase
      .from('leads_cagliari')
      .update({ callback_date: dateValue })
      .eq('id', id)

    if (updateError) {
      setError('Errore nel salvataggio del richiamo')
      return
    }

    setLeads((prev) =>
      prev.map((lead) => (lead.id === id ? { ...lead, callback_date: dateValue } : lead))
    )
    setCallbackPickerLeadId(null)
    setCallbackDateDraft('')

    if (dateValue) {
      logLeadActivity(id, 'callback_set', { date: dateValue })
      // Auto-set status to callback if not already
      const lead = leads.find((l) => l.id === id)
      if (lead && lead.status !== 'callback' && lead.status !== 'acquired') {
        updateLeadStatus(id, 'callback')
      }
    } else {
      logLeadActivity(id, 'callback_removed')
    }
  }

  const removeCallback = async (id: string) => {
    await supabase.from('leads_cagliari').update({ callback_date: null }).eq('id', id)
    setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, callback_date: null } : lead)))
    logLeadActivity(id, 'callback_removed')
  }

  // ─── Tags ───────────────────────────────────────────────────────────────
  const addTag = async (id: string, tag: string) => {
    const lead = leads.find((l) => l.id === id)
    const currentTags = lead?.tags || []
    if (currentTags.includes(tag)) return

    const newTags = [...currentTags, tag]
    const { error: updateError } = await supabase
      .from('leads_cagliari')
      .update({ tags: newTags })
      .eq('id', id)

    if (updateError) { setError('Errore nel salvataggio tag'); return }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, tags: newTags } : l)))
    logLeadActivity(id, 'tag_added', { tag })
  }

  const removeTag = async (id: string, tag: string) => {
    const lead = leads.find((l) => l.id === id)
    const newTags = (lead?.tags || []).filter((t) => t !== tag)
    await supabase.from('leads_cagliari').update({ tags: newTags }).eq('id', id)
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, tags: newTags } : l)))
    logLeadActivity(id, 'tag_removed', { tag })
  }

  // ─── Navigate to lead from dashboard ────────────────────────────────────
  const navigateToLead = (leadId: string) => {
    setActiveTabRaw('leads')
    setExpandedLeadId(leadId)
  }

  const uniqueZones = useMemo(() => {
    const zones = new Set<string>()
    leads.forEach(l => {
      if (l.valuation?.zone_name_csv) zones.add(l.valuation.zone_name_csv)
    })
    return Array.from(zones).sort()
  }, [leads])

  const setActiveTab = useCallback((tab: typeof activeTab) => {
    if (quotazioniDirty && activeTab === 'quotazioni' && tab !== 'quotazioni') {
      if (!window.confirm('Hai modifiche non salvate nelle Quotazioni.\nSei sicuro di voler cambiare tab? Le modifiche andranno perse.')) {
        return
      }
    }
    setActiveTabRaw(tab)
  }, [activeTab, quotazioniDirty])

  // Carica zone per export quando si è nel tab quotazioni
  useEffect(() => {
    if (activeTab === 'quotazioni') {
      fetchZoneQuotazioni().then(setExportZones)
    }
  }, [activeTab])

  const handleLogout = async () => {
    if (window.confirm('Sei sicuro di voler uscire?')) {
      await logout()
      navigate('/admin/login')
    }
  }

  const filteredLeads = useMemo(() => {
    const filtered = leads.filter(l => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const matches = [
          l.first_name, l.last_name, l.email, l.phone,
          l.valuation?.address, l.valuation?.zone_name_csv,
          ...(l.tags || []),
        ].some(field => field?.toLowerCase().includes(q))
        if (!matches) return false
      }
      if (statusFilter !== 'all' && l.status !== statusFilter) return false
      if (zoneFilter !== 'all' && l.valuation?.zone_name_csv !== zoneFilter) return false
      return true
    })
    if (sortBy === 'score') {
      filtered.sort((a, b) => computeLeadScore(b) - computeLeadScore(a))
    }
    return filtered
  }, [leads, searchQuery, statusFilter, zoneFilter, sortBy])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-lg animated-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in-up">
            <div className="flex items-center gap-4">
              <Link to="/valutazione" className="flex items-center gap-3 group">
                <div className="bg-primary-600 p-2 rounded-xl transition-transform group-hover:scale-110">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Dashboard Admin</h1>
                  <p className="text-gray-400 text-sm">
                    ImmobiliareAI {CITY_NAME} - Gestione Lead
                  </p>
                </div>
              </Link>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={handleLogout}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
              {notificationPermission === 'default' && (
                <button
                  onClick={requestNotificationPermission}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-yellow-300 text-xs font-medium hover:bg-yellow-500/30 transition-colors"
                >
                  <Bell className="h-3.5 w-3.5" />
                  Abilita notifiche
                </button>
              )}
              {notificationPermission === 'denied' && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-full text-red-300 text-xs font-medium">
                  <BellOff className="h-3.5 w-3.5" />
                  Notifiche bloccate
                </div>
              )}
              {aiAssistantEnabled && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                  <Bot className="h-3.5 w-3.5 text-emerald-300" />
                  <span className="text-emerald-300 text-xs font-medium">AI Attivo</span>
                </div>
              )}
              {isConnected && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                  <span className="text-green-300 text-xs font-medium">Live</span>
                </div>
              )}
              {newLeadsCount > 0 && (
                <button
                  onClick={resetNewLeadsCount}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-300 text-xs font-medium hover:bg-orange-500/30 transition-colors"
                >
                  +{newLeadsCount} nuov{newLeadsCount === 1 ? 'o' : 'i'}
                </button>
              )}
              <Button variant="outline" onClick={() => { fetchLeads(); resetNewLeadsCount(); }} disabled={isLoading} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <ExportDropdown
                leads={filteredLeads}
                allLeads={leads}
                activeTab={activeTab}
                disabled={leads.length === 0 && activeTab !== 'quotazioni'}
                zones={exportZones}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex gap-1 bg-gray-200 p-1 rounded-lg w-fit overflow-x-auto">
          <button
            onClick={() => setActiveTab('oggi')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'oggi'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Clock className="h-4 w-4" />
            Oggi
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'leads'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="h-4 w-4" />
            Lead
          </button>
          <button
            onClick={() => setActiveTab('statistiche')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'statistiche'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Statistiche
          </button>
          <button
            onClick={() => setActiveTab('conversazioni')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'conversazioni'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Bot className="h-4 w-4" />
            Chat AI
          </button>
          <button
            onClick={() => setActiveTab('quotazioni')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'quotazioni'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Settings className="h-4 w-4" />
            Quotazioni
          </button>
          <button
            onClick={() => setActiveTab('correzioni')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'correzioni'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            Correzioni OMI
          </button>
          <button
            onClick={() => setActiveTab('impostazioni')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'impostazioni'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Sliders className="h-4 w-4" />
            Impostazioni
          </button>
        </div>
      </div>

      {/* Dashboard Oggi Tab */}
      {activeTab === 'oggi' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 tab-content-enter">
          <DashboardOggi leads={leads} onLeadClick={navigateToLead} />
        </div>
      )}

      {/* Statistiche Tab */}
      {activeTab === 'statistiche' && (
        <div className="tab-content-enter">
          <StatisticheDashboard leads={leads} />
        </div>
      )}

      {/* Conversazioni AI Tab */}
      {activeTab === 'conversazioni' && (
        <div className="tab-content-enter">
          <ConversazioniPanel />
        </div>
      )}

      {/* Quotazioni Tab */}
      {activeTab === 'quotazioni' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 tab-content-enter">
          <QuotazioniEditor onDirtyChange={setQuotazioniDirty} />
        </div>
      )}

      {/* Correzioni OMI Tab */}
      {activeTab === 'correzioni' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 tab-content-enter">
          <OmiCorrezioniPanel />
        </div>
      )}

      {/* Impostazioni Tab */}
      {activeTab === 'impostazioni' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 tab-content-enter">
          <ImpostazioniPanel onAiStatusChange={setAiAssistantEnabled} />
        </div>
      )}

      {/* Stats */}
      {activeTab === 'leads' && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 tab-content-enter">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 admin-stagger">
          <Card className="border-0 shadow-md admin-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Totale Lead</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{leads.length}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full admin-kpi-icon">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md admin-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Con Telefono</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {leads.filter((l) => l.phone).length}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full admin-kpi-icon">
                  <Phone className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md admin-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Acquisiti</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {leads.filter((l) => l.status === 'acquired').length}
                  </p>
                </div>
                <div className="bg-emerald-100 p-3 rounded-full admin-kpi-icon">
                  <UserCheck className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md admin-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Valore Medio</p>
                  <p className="text-2xl font-bold text-primary-600 mt-1">
                    {leads.length > 0
                      ? formatCurrency(
                          leads.reduce((acc, l) => acc + (l.valuation?.estimated_value || 0), 0) /
                            leads.filter((l) => l.valuation).length || 0
                        )
                      : '-'}
                  </p>
                </div>
                <div className="bg-orange-100 p-3 rounded-full admin-kpi-icon">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, email, telefono, indirizzo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-1 bg-gray-200 p-1 rounded-lg">
            {([['all', 'Tutti'], ['new', 'Nuovi'], ['contacted', 'Contattati'], ['callback', 'Richiamare'], ['acquired', 'Acquisiti']] as const).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <select
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">Tutte le zone</option>
            {uniqueZones.map(zone => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>
          <button
            onClick={() => setSortBy(sortBy === 'date' ? 'score' : 'date')}
            className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${
              sortBy === 'score'
                ? 'border-amber-300 bg-amber-50 text-amber-700'
                : 'border-gray-300 text-gray-600 hover:text-gray-900'
            }`}
            title="Ordina per punteggio lead"
          >
            <Star className="h-3.5 w-3.5" fill={sortBy === 'score' ? 'currentColor' : 'none'} />
            {sortBy === 'score' ? 'Per score' : 'Per data'}
          </button>
        </div>

        {/* Section Title */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Lead recenti
          </h2>
          <span className="text-sm text-gray-500">
            {filteredLeads.length === leads.length
              ? `Ultimi ${leads.length} contatti`
              : `${filteredLeads.length} di ${leads.length} contatti`}
          </span>
        </div>

        {/* Leads List */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="h-10 w-10 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-500">Caricamento lead...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-16 text-center">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun lead trovato</h3>
              <p className="text-gray-500 mb-4">I lead appariranno qui quando gli utenti completeranno il form di valutazione.</p>
              <Button onClick={() => { fetchLeads(); resetNewLeadsCount(); }}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 lead-stagger">
            {filteredLeads.map((lead, index) => (
              <Card key={lead.id} className="overflow-hidden border-0 shadow-md admin-card" style={{ animationDelay: `${Math.min(index * 0.05, 0.5)}s` }}>
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                    {/* Contact Info */}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {lead.first_name || lead.last_name
                                ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                                : 'Nome non specificato'}
                            </h3>
                            {/* Lead Score Stars */}
                            <div className="flex items-center gap-0.5" title={`Score: ${computeLeadScore(lead)}/5`}>
                              {Array.from({ length: 5 }).map((_, i) => {
                                const score = computeLeadScore(lead)
                                return (
                                  <Star
                                    key={i}
                                    className={`h-3 w-3 ${i < score ? scoreColor(score) : 'text-gray-200'}`}
                                    fill={i < score ? 'currentColor' : 'none'}
                                  />
                                )
                              })}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(lead.created_at)}
                            {lead.source === 'ai_chat' ? (
                              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded">Chat AI</span>
                            ) : (
                              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">Form</span>
                            )}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs rounded-full transition-all ${
                            lead.status === 'new'
                              ? 'bg-green-100 text-green-800 badge-new'
                              : lead.status === 'contacted'
                              ? 'bg-blue-100 text-blue-800'
                              : lead.status === 'callback'
                              ? 'bg-orange-100 text-orange-800'
                              : lead.status === 'acquired'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {lead.status === 'new' ? 'Nuovo' : lead.status === 'contacted' ? 'Contattato' : lead.status === 'callback' ? 'Da richiamare' : lead.status === 'acquired' ? 'Acquisito' : lead.status}
                        </span>
                      </div>

                      {/* Callback banner */}
                      {lead.callback_date && (
                        <div className={`mb-3 px-3 py-2 rounded-lg text-xs flex items-center justify-between ${
                          isCallbackOverdue(lead.callback_date) ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'
                        }`}>
                          <span className={isCallbackOverdue(lead.callback_date) ? 'text-red-700 font-medium' : 'text-orange-700'}>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatCallbackDate(lead.callback_date)}
                            {isCallbackOverdue(lead.callback_date) && ' — SCADUTO'}
                          </span>
                          <button onClick={() => removeCallback(lead.id)} className="text-gray-400 hover:text-red-500">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}

                      {/* Tags */}
                      {(lead.tags && lead.tags.length > 0) && (
                        <div className="mb-3 flex flex-wrap gap-1">
                          {lead.tags.map((tag) => (
                            <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${getTagColor(tag)}`}>
                              {tag}
                              <button onClick={() => removeTag(lead.id, tag)} className="hover:opacity-70"><X className="h-2.5 w-2.5" /></button>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2">
                        <a
                          href={`mailto:${lead.email}`}
                          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
                        >
                          <Mail className="h-4 w-4" />
                          {lead.email}
                        </a>
                        {lead.phone && (
                          <div className="flex items-center gap-2">
                            <a
                              href={`tel:${lead.phone}`}
                              className="flex items-center gap-2 text-green-600 hover:text-green-700"
                            >
                              <Phone className="h-4 w-4" />
                              {lead.phone}
                            </a>
                            {/* WhatsApp Quick Link */}
                            <a
                              href={buildWhatsAppUrl(lead.phone, [lead.first_name, lead.last_name].filter(Boolean).join(' '), lead.valuation?.zone_name_csv)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                              title="Apri WhatsApp"
                            >
                              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              WA
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {lead.gdpr_consent && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            GDPR ✓
                          </span>
                        )}
                        {lead.marketing_consent && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">
                            Marketing ✓
                          </span>
                        )}
                      </div>

                      {/* Azioni lead */}
                      <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'contacted')}
                          disabled={lead.status === 'contacted'}
                          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                            lead.status === 'contacted'
                              ? 'bg-blue-50 text-blue-400 cursor-not-allowed'
                              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                          }`}
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Contattato
                        </button>
                        <button
                          onClick={() => {
                            setCallbackPickerLeadId(callbackPickerLeadId === lead.id ? null : lead.id)
                            setCallbackDateDraft(lead.callback_date ? lead.callback_date.slice(0, 16) : '')
                          }}
                          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                            lead.callback_date
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                          }`}
                        >
                          <PhoneCall className="h-3.5 w-3.5" />
                          Richiamare
                        </button>
                        <button
                          onClick={() => updateLeadStatus(lead.id, 'acquired')}
                          disabled={lead.status === 'acquired'}
                          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                            lead.status === 'acquired'
                              ? 'bg-emerald-50 text-emerald-400 cursor-not-allowed'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }`}
                        >
                          <UserCheck className="h-3.5 w-3.5" />
                          Acquisito
                        </button>
                        <button
                          onClick={() => setTagPickerLeadId(tagPickerLeadId === lead.id ? null : lead.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                        >
                          <Tag className="h-3.5 w-3.5" />
                          Tag
                        </button>
                        <button
                          onClick={() => setTemplateLeadId(lead.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          Msg
                        </button>
                        <button
                          onClick={() => exportSingleLeadPDF(lead)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          PDF
                        </button>
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Callback date picker (inline) */}
                      {callbackPickerLeadId === lead.id && (
                        <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <label className="text-xs font-medium text-gray-700 block mb-1">Data e ora richiamo</label>
                          <div className="flex gap-2">
                            <input
                              type="datetime-local"
                              value={callbackDateDraft}
                              onChange={(e) => setCallbackDateDraft(e.target.value)}
                              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <button
                              onClick={() => saveCallback(lead.id)}
                              disabled={!callbackDateDraft}
                              className="px-3 py-1.5 text-xs font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
                            >
                              Salva
                            </button>
                            <button
                              onClick={() => setCallbackPickerLeadId(null)}
                              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Tag picker (inline) */}
                      {tagPickerLeadId === lead.id && (
                        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <label className="text-xs font-medium text-gray-700 block mb-2">Aggiungi etichetta</label>
                          <div className="flex flex-wrap gap-1.5">
                            {TAG_PRESETS.map((preset) => {
                              const alreadyHas = (lead.tags || []).includes(preset.label)
                              return (
                                <button
                                  key={preset.label}
                                  onClick={() => { if (!alreadyHas) addTag(lead.id, preset.label) }}
                                  disabled={alreadyHas}
                                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                                    alreadyHas ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'
                                  } ${preset.color}`}
                                >
                                  {preset.label}
                                </button>
                              )
                            })}
                          </div>
                          <button onClick={() => setTagPickerLeadId(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Chiudi</button>
                        </div>
                      )}

                      {/* Notes */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        {editingNotes === lead.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                              rows={3}
                              placeholder="Aggiungi note..."
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => saveNotes(lead.id)}
                                className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                              >
                                Salva
                              </button>
                              <button
                                onClick={() => setEditingNotes(null)}
                                className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
                              >
                                Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingNotes(lead.id); setNotesDraft(lead.notes || '') }}
                            className="w-full text-left"
                          >
                            {lead.notes ? (
                              <div className="flex items-start gap-1.5">
                                <StickyNote className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{lead.notes}</p>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic flex items-center gap-1.5">
                                <StickyNote className="h-3.5 w-3.5" />
                                Aggiungi note...
                              </p>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Timeline (collapsible) */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => setExpandedLeadId(expandedLeadId === lead.id ? null : lead.id)}
                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                          <Clock className="h-3 w-3" />
                          {expandedLeadId === lead.id ? 'Nascondi storico' : 'Mostra storico'}
                        </button>
                        {expandedLeadId === lead.id && (
                          <div className="mt-2">
                            <LeadTimeline leadId={lead.id} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Property Info */}
                    {lead.valuation && (
                      <>
                        <div className="p-5 bg-gray-50">
                          <h4 className="text-sm font-medium text-gray-500 mb-3">
                            Immobile
                          </h4>
                          <div className="space-y-2">
                            <p className="flex items-center gap-2 text-gray-900">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              {lead.valuation.address}
                            </p>
                            <p className="flex items-center gap-2 text-gray-700">
                              <Home className="h-4 w-4 text-gray-400" />
                              {lead.valuation.property_type} - {lead.valuation.area} mq
                            </p>
                            <p className="text-sm text-gray-500">
                              Zona: <span className="font-medium">{lead.valuation.zone_name_csv}</span>
                            </p>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-1">
                            {lead.valuation.has_elevator && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Ascensore</span>
                            )}
                            {lead.valuation.has_pertinenza && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Pertinenza</span>
                            )}
                            {lead.valuation.has_good_exposure && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Esposizione</span>
                            )}
                            {lead.valuation.has_external_spaces && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Spazi esterni</span>
                            )}
                            {lead.valuation.has_garage && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">
                                {lead.valuation.garage_type === 'posto_auto' ? 'Posto auto' : 'Box'}
                              </span>
                            )}
                            {lead.valuation.has_pool && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Piscina</span>
                            )}
                            {lead.valuation.is_residence && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Residence</span>
                            )}
                            {lead.valuation.is_recente && (
                              <span className="px-2 py-0.5 text-xs bg-emerald-50 border border-emerald-200 rounded text-emerald-700">Recente</span>
                            )}
                            {lead.valuation.energy_class === 'alta' && (
                              <span className="px-2 py-0.5 text-xs bg-emerald-50 border border-emerald-200 rounded text-emerald-700">Classe A/B</span>
                            )}
                            {lead.valuation.energy_class === 'media' && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Classe C/D/E</span>
                            )}
                            {lead.valuation.energy_class === 'bassa' && (
                              <span className="px-2 py-0.5 text-xs bg-amber-50 border border-amber-200 rounded text-amber-700">Classe F/G</span>
                            )}
                            {lead.valuation.special_type === 'attico' && (
                              <span className="px-2 py-0.5 text-xs bg-primary-50 border border-primary-200 rounded text-primary-700">Attico</span>
                            )}
                            {lead.valuation.special_type === 'loft' && (
                              <span className="px-2 py-0.5 text-xs bg-primary-50 border border-primary-200 rounded text-primary-700">Loft</span>
                            )}
                            {lead.valuation.special_type === 'monolocale' && (
                              <span className="px-2 py-0.5 text-xs bg-primary-50 border border-primary-200 rounded text-primary-700">Monolocale</span>
                            )}
                            {lead.valuation.has_terrazzo_grande && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Terrazzo &gt;20mq</span>
                            )}
                            {lead.valuation.has_giardino_privato && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Giardino &gt;50mq</span>
                            )}
                            {lead.valuation.has_posto_barca && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Posto barca</span>
                            )}
                            {lead.valuation.has_domotica && (
                              <span className="px-2 py-0.5 text-xs bg-white border rounded">Domotica</span>
                            )}
                          </div>
                        </div>

                        {/* Valuation */}
                        <div className="p-5">
                          <h4 className="text-sm font-medium text-gray-500 mb-3">
                            Valutazione
                          </h4>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-primary-600 flex items-center justify-center gap-1">
                              <Euro className="h-6 w-6" />
                              {formatCurrency(lead.valuation.estimated_value)}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatCurrency(lead.valuation.min_value)} - {formatCurrency(lead.valuation.max_value)}
                            </p>
                            <p className="text-sm font-medium text-gray-700 mt-2">
                              {formatCurrency(lead.valuation.price_per_sqm)}/mq
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>}

      {/* Template Messages Modal */}
      {templateLeadId && (() => {
        const lead = leads.find((l) => l.id === templateLeadId)
        return lead ? (
          <TemplateMessages lead={lead} onClose={() => setTemplateLeadId(null)} />
        ) : null
      })()}
    </div>
  )
}
